import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const zipProcurement = defineConnector({
  identifier: "zipProcurement",
  name: "Zip (procurement)",
  tagLine: "Purchasing requests and approvals",
  keywords: "zip.com ziphq procurement intake purchasing approvals vendors",
  logo,
  tags: ["data"],
  protocol: "http",
  defaultUrlPattern: ({ apiHost }) =>
    `^${exactUrlToRegex(`https://${apiHost || "api.ziphq.com"}`)}$`,
  auth: (app) =>
    headerAuth([
      {
        name: app.configuration.headerName || "Zip-Api-Key",
        value: app.secret.apiKey,
        scheme: app.configuration.scheme || undefined,
      },
      ...(app.configuration.apiVersion
        ? [{ name: "Zip-Api-Version", value: app.configuration.apiVersion }]
        : []),
    ]),
  steps: [
    {
      title: "Find your Zip procurement API details",
      items: [
        {
          kind: "markdown",
          content:
            "This connector is for Zip’s procurement platform at zip.com. In your Zip account, open **API Documentation** from the question mark menu. Create an API key with access to the resources your workflows need, and check your organization’s API guide for its hostname, key header, and supported API version.",
        },
      ],
    },
    {
      title: "Enter your Zip procurement API details",
      items: [
        {
          kind: "hostname",
          label: "API hostname",
          target: { kind: "configuration", field: "apiHost" },
          defaultValue: "api.ziphq.com",
          required: true,
          helperText:
            "Use the hostname shown in your Zip API documentation if it differs.",
        },
        {
          kind: "input",
          label: "Authentication header name",
          target: { kind: "configuration", field: "headerName" },
          defaultValue: "Zip-Api-Key",
          required: true,
          helperText:
            "Use the header name from your Zip API documentation if it differs.",
        },
        {
          kind: "input",
          label: "Header prefix",
          target: { kind: "configuration", field: "scheme" },
          helperText:
            "If your API guide requires a prefix such as Bearer, enter it here. Otherwise, leave this blank.",
        },
        {
          kind: "input",
          label: "API version",
          target: { kind: "configuration", field: "apiVersion" },
          defaultValue: "2024-06-06",
          placeholder: "YYYY-MM-DD",
          helperText:
            "Use the version in your Zip API documentation. Clear this field to omit the Zip-Api-Version header.",
        },
        {
          kind: "input",
          label: "API key",
          target: { kind: "secrets", field: "apiKey" },
          required: true,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ apiHost }) => ({
      url: `https://${apiHost || "api.ziphq.com"}/users?page_size=1`,
      expectedStatus: [200, 403],
    }),
  },
  usageHint: ({ apiHost }) =>
    `Use the Zip procurement API at https://${apiHost || "api.ziphq.com"}/. The configured API key header is added automatically.`,
});
